// handoffs.js — Handoffs / Messages page machinery.
//
// Owns the thread-list / compose-form / thread-drawer renders,
// the load+resolve+send wire calls, and the unread-badge refresh.
// All of it was originally tangled in bootstrap.js (~600
// lines); hoisting it here moves the entire A2A handoffs feature
// into a single module with explicit dependencies.
//
// Public exports (consumed by pages/handoffs.js +
// pages/coordination.js + legacy bridge during transition):
//   - loadHandoffThreads, drawHandoffsBody
//   - startHandoffCompose, cancelHandoffCompose
//   - drawHandoffCompose, submitHandoffCompose
//   - expandHandoffThread, loadHandoffThread, drawHandoffsExpanded
//   - resolveHandoffThread, refreshHandoffsBadge
//
// Private:
//   - isHandoffsCloudMode (alias for isCloudMode), failCompose,
//     loadHandoffRecipients

import { state } from "./state.js";
import { handoffsState } from "./page-state.js";
import { patchHTML } from "./dom.js";
import { escapeHTML, ago } from "./util.js";
import { isCloudMode, showToast } from "./ui.js";
import { api } from "./api.js";
import { decryptHint, encryptHint } from "./crypto.js";
import { chatLabel, friendlyResource, parseAgentID } from "./format.js";
import { navigate, routeURL } from "./router.js";

// isHandoffsCloudMode: thin alias kept so the function name reads
// naturally at call sites ("are we showing the cloud-mode endpoint
// for this page?").
function isHandoffsCloudMode() {
  return isCloudMode();
}

// whoami() is the dashboard's cached identity for cloud-mode
// "from agent_id" defaults. bootstrap.js's auth flow
// populates an internal `whoamiCache` let; the bridge exposes a
// getter (getWhoami) so the read resolves at call time (after
// auth init has finished) rather than capturing the initial null.
function whoami() {
  const getter = window.AC_LEGACY && window.AC_LEGACY.getWhoami;
  if (typeof getter === "function") return getter();
  return null;
}

// ---- thread list ----

export async function loadHandoffThreads(opts) {
  // Background callers — the badge prefetch and poller in bootstrap.js — pass
  // { background: true } so a 401 can't sign the user out. Page renders and
  // explicit refreshes pass nothing and keep the old behavior (401 → login),
  // which is what a user-initiated read should do.
  const apiOpts = opts && opts.background ? { background: true } : undefined;
  if (isHandoffsCloudMode()) {
    const data = await api("/v1/teams/handoffs/threads?limit=100", apiOpts);
    const raw = (data && data.threads) || [];
    // Decrypt subject + body + re_resource hints using the team
    // HMAC key. Same pattern leases/events use.
    return await Promise.all(
      raw.map(async (t) => ({
        context_id: t.context_id,
        message_count: t.message_count,
        // Cloud doesn't roll up unread_count yet (pulled rows are
        // already removed from outbound queue) — show 0 until PR
        // 3.next adds inbox state mirroring.
        unread_count: 0,
        last_from: t.last_from,
        last_to: t.last_to,
        last_state: t.last_state,
        last_at: t.last_at,
        subject: t.last_subject_hint
          ? (await decryptHint(t.last_subject_hint)) || ""
          : "",
        last_body: t.last_body_hint
          ? (await decryptHint(t.last_body_hint)) || "(encrypted)"
          : "",
        last_re_resource: t.last_re_resource_hint
          ? (await decryptHint(t.last_re_resource_hint)) || ""
          : "",
        last_from_machine: t.last_from_machine,
      })),
    );
  }
  const data = await api("/v1/a2a/threads?limit=100", apiOpts);
  return (data && data.threads) || [];
}

export function drawHandoffsBody() {
  const body = document.getElementById("handoffs-body");
  if (!body) return;
  if (handoffsState.composing) {
    drawHandoffCompose();
    return;
  }
  if (handoffsState.expanded) {
    drawHandoffsExpanded();
    return;
  }
  // Compose-affordance bar at the top of the list.
  const composeBar = `
    <div style="padding: 10px 16px; border-bottom: 1px solid var(--border); display: flex; gap: 8px; align-items: center;">
      <button id="handoffs-new-btn"
              style="padding: 6px 12px; border: 1px solid var(--accent); background: var(--accent); color: #fff; font-size: 12px; font-weight: 600; border-radius: var(--r-md); cursor: pointer;">
        + New handoff
      </button>
      <span style="font-size: 11px; color: var(--text-tertiary);">Send a note to another chat. It sees it before its next edit.</span>
    </div>`;
  if (handoffsState.threads.length === 0) {
    patchHTML(
      body,
      composeBar +
        `<div class="empty">
          <div class="empty-title">No handoffs yet</div>
          <div class="empty-detail">
            Two parallel chats can hand off context here instead of you copy/pasting.
            Click <strong>+ New handoff</strong> above, or call
            <code>agentcollision.handoff.send</code> from any agent — both appear in this list within seconds.
          </div>
        </div>`,
    );
    const newBtn = document.getElementById("handoffs-new-btn");
    if (newBtn) newBtn.onclick = startHandoffCompose;
    return;
  }
  let html = composeBar;
  for (const t of handoffsState.threads) {
    const unreadDot =
      t.unread_count > 0
        ? `<span class="badge" style="background: var(--accent); color: #fff;">${t.unread_count}</span>`
        : "";
    const reChip = t.last_re_resource
      ? `<code style="font-size: 11px; padding: 1px 6px; background: var(--bg-soft); border-radius: 3px; color: var(--text-muted);">re ${escapeHTML(friendlyResource(t.last_re_resource))}</code>`
      : "";
    const machineChip = t.last_from_machine
      ? `<code style="font-size: 10px; color: var(--text-tertiary);">${escapeHTML((t.last_from_machine || "").slice(0, 12))}</code>`
      : "";
    html += `
      <div style="padding: 12px 16px; border-bottom: 1px solid var(--border); cursor: pointer;"
           data-thread-context="${escapeHTML(t.context_id)}">
        <div style="display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 4px;">
          <strong style="font-size: 13px;">${escapeHTML(t.subject || "(no subject)")}</strong>
          ${unreadDot}
          <span style="font-size: 11px; color: var(--text-muted); margin-left: auto;">${escapeHTML(ago(t.last_at))}</span>
        </div>
        <div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 4px;">
          <strong>${escapeHTML(t.last_from)}</strong> → ${escapeHTML(t.last_to || "Everyone")}
          <span style="color: var(--text-tertiary); margin-left: 6px;">· ${t.message_count} msg${t.message_count === 1 ? "" : "s"}</span>
        </div>
        <div style="font-size: 12px; color: var(--text-secondary); line-height: 1.4;">${escapeHTML((t.last_body || "").slice(0, 220))}</div>
        <div style="font-size: 11px; color: var(--text-tertiary); margin-top: 4px; display: flex; gap: 8px; align-items: center;">
          ${reChip}
          ${machineChip}
        </div>
      </div>`;
  }
  patchHTML(body, html);
  const newBtn = document.getElementById("handoffs-new-btn");
  if (newBtn) newBtn.onclick = startHandoffCompose;
  body.querySelectorAll("[data-thread-context]").forEach((el) => {
    el.onclick = () => expandHandoffThread(el.dataset.threadContext);
  });
}

// ---- compose ----

export function startHandoffCompose(parentTaskID) {
  handoffsState.composing = true;
  handoffsState.replyingTo =
    typeof parentTaskID === "string" ? parentTaskID : null;
  drawHandoffCompose();
}

export function cancelHandoffCompose() {
  handoffsState.composing = false;
  handoffsState.replyingTo = null;
  drawHandoffsBody();
}

// drawHandoffCompose paints the form for authoring a new handoff
// (or a reply, when replyingTo is set).
export async function drawHandoffCompose() {
  const body = document.getElementById("handoffs-body");
  if (!body) return;
  const replying = !!handoffsState.replyingTo;
  let parentRow = null;
  if (replying) {
    parentRow =
      (handoffsState.expandedRows || []).find(
        (h) => h.task_id === handoffsState.replyingTo,
      ) || null;
  }
  const recipients = await loadHandoffRecipients();
  let recipientOptions = "";
  if (replying && parentRow) {
    recipientOptions = `<option value="${escapeHTML(parentRow.from_agent_id)}" selected>${escapeHTML(parentRow.from_agent_id)} (replying)</option>`;
  } else {
    recipientOptions = '<option value="">— pick a recipient —</option>';
    for (const r of recipients) {
      recipientOptions += `<option value="${escapeHTML(r.agent_id)}">${escapeHTML(r.label || r.agent_id)}</option>`;
    }
  }

  const cloudWarning =
    isHandoffsCloudMode() && !state.hmacKey
      ? `<div class="help-block" style="margin-bottom: 12px; color: var(--danger);">
           Team key not loaded. Sending from the website needs your team key (loaded
           automatically when you are signed in to a team) so the message can be scrambled
           before it is sent.
         </div>`
      : "";

  // Pre-fill the From field with the best identity we have.
  const w = whoami();
  const cloudFromDefault = (() => {
    if (w && w.email) return "dashboard/" + w.email;
    if (state.deviceLabel) return "dashboard/" + state.deviceLabel;
    return "";
  })();
  const fromHint = isHandoffsCloudMode()
    ? `<input type="text" id="handoff-compose-from" class="prompt-input" value="${escapeHTML(cloudFromDefault)}" placeholder="dashboard/your-email" style="width: 100%;" />`
    : `<input type="text" id="handoff-compose-from" class="prompt-input" placeholder="claude-code/your-session-id" style="width: 100%;" />`;

  patchHTML(
    body,
    `<div style="padding: 16px 20px;">
      <div style="display: flex; gap: 8px; align-items: center; margin-bottom: 12px;">
        <button id="handoff-compose-cancel"
                style="padding: 4px 10px; border: 1px solid var(--border); background: var(--bg-soft); border-radius: var(--r-sm); cursor: pointer; font-size: 12px;">← Cancel</button>
        <strong style="font-size: 14px;">${replying ? "Reply to handoff" : "New handoff"}</strong>
      </div>
      ${cloudWarning}
      <form id="handoff-compose-form" style="display: flex; flex-direction: column; gap: 12px;">
        <label style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary);">
          From (your session)
          ${fromHint}
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary);">
          To (the other session)
          <select id="handoff-compose-to" class="prompt-input" style="width: 100%;" ${replying ? "disabled" : ""}>
            ${recipientOptions}
          </select>
          <span style="font-size: 11px; color: var(--text-tertiary);">Pick from the chats active on this team. The other chat sees the message before its next edit.</span>
        </label>
        ${replying
          ? ""
          : `
        <label style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary);">
          Subject (optional)
          <input type="text" id="handoff-compose-subject" class="prompt-input" maxlength="512" style="width: 100%;" placeholder="One-line summary, shown in the inbox listing" />
        </label>
        <label style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary);">
          Re: resource (optional)
          <input type="text" id="handoff-compose-re" class="prompt-input" style="width: 100%;" placeholder="e.g. file:/repo:src/auth/login.ts — surfaced as a chip" />
        </label>`}
        <label style="display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: var(--text-secondary);">
          Message
          <textarea id="handoff-compose-body" class="prompt-input" style="width: 100%; min-height: 140px; resize: vertical; font-family: var(--font-mono);" placeholder="${replying ? "Your reply text…" : "What does the recipient need to know? Plain text, up to 32 KiB."}"></textarea>
        </label>
        <div style="display: flex; gap: 8px; align-items: center;">
          <button type="submit" id="handoff-compose-send"
                  style="padding: 8px 14px; border: 1px solid var(--accent); background: var(--accent); color: #fff; font-size: 13px; font-weight: 600; border-radius: var(--r-md); cursor: pointer;">
            ${replying ? "Send reply" : "Send handoff"}
          </button>
          <span id="handoff-compose-status" style="font-size: 12px; color: var(--text-tertiary);"></span>
        </div>
      </form>
    </div>`,
  );
  document
    .getElementById("handoff-compose-cancel")
    .addEventListener("click", cancelHandoffCompose);
  document
    .getElementById("handoff-compose-form")
    .addEventListener("submit", async (e) => {
      e.preventDefault();
      await submitHandoffCompose(parentRow);
    });

  // Disable Send until every required field is non-empty.
  const fromInput = document.getElementById("handoff-compose-from");
  const toInput = document.getElementById("handoff-compose-to");
  const bodyInput = document.getElementById("handoff-compose-body");
  const sendBtn = document.getElementById("handoff-compose-send");
  function refreshSendEnabled() {
    const fromOK = !!(
      fromInput &&
      fromInput.value &&
      fromInput.value.trim()
    );
    const toOK =
      replying ||
      !!(toInput && toInput.value && toInput.value.trim());
    const bodyOK = !!(
      bodyInput &&
      bodyInput.value &&
      bodyInput.value.trim()
    );
    const ok = fromOK && toOK && bodyOK;
    sendBtn.disabled = !ok;
    sendBtn.style.opacity = ok ? "1" : "0.5";
    sendBtn.style.cursor = ok ? "pointer" : "not-allowed";
  }
  [fromInput, toInput, bodyInput].forEach((el) => {
    if (!el) return;
    el.addEventListener("input", refreshSendEnabled);
    el.addEventListener("change", refreshSendEnabled);
  });
  refreshSendEnabled();
}

// loadHandoffRecipients fetches the candidate recipient list.
async function loadHandoffRecipients() {
  try {
    if (isHandoffsCloudMode()) {
      const data = await api("/v1/teams/heartbeats");
      const rows = (data && data.heartbeats) || [];
      return rows.map((r) => ({
        agent_id: r.agent_id,
        label:
          r.agent_id + (r.last_tool ? ` · ${r.last_tool}` : ""),
      }));
    }
    // The daemon's /v1/agents answers in Go field names (AgentID, Runtime,
    // LastSeenAt).
    const data = await api("/v1/agents");
    const rows = (data && data.agents) || [];
    rows.sort((a, b) =>
      (b.LastSeenAt || "").localeCompare(a.LastSeenAt || ""),
    );
    return rows.map((r) => ({
      agent_id: r.AgentID,
      label: `${r.AgentID} · ${r.Runtime || "unknown"}`,
    }));
  } catch {
    return [];
  }
}

export async function submitHandoffCompose(parentRow) {
  const status = document.getElementById("handoff-compose-status");
  const sendBtn = document.getElementById("handoff-compose-send");
  if (status) {
    status.style.color = "var(--text-tertiary)";
    status.textContent = "Sending…";
  }
  if (sendBtn) sendBtn.disabled = true;

  const fromAgentID = (
    document.getElementById("handoff-compose-from")?.value || ""
  ).trim();
  const toAgentID =
    (document.getElementById("handoff-compose-to")?.value || "").trim() ||
    (parentRow ? parentRow.from_agent_id : "");
  const bodyText = (
    document.getElementById("handoff-compose-body")?.value || ""
  ).trim();
  const subject = (
    document.getElementById("handoff-compose-subject")?.value || ""
  ).trim();
  const reResource = (
    document.getElementById("handoff-compose-re")?.value || ""
  ).trim();
  const replying = !!handoffsState.replyingTo;

  if (!fromAgentID) {
    failCompose(status, sendBtn, "from required");
    return;
  }
  if (!toAgentID && !replying) {
    failCompose(status, sendBtn, "to required");
    return;
  }
  if (!bodyText) {
    failCompose(status, sendBtn, "message required");
    return;
  }

  try {
    if (isHandoffsCloudMode()) {
      if (!state.hmacKey) throw new Error("team key not loaded");
      const bodyHint = await encryptHint(bodyText);
      const subjectHint = subject ? await encryptHint(subject) : "";
      const reHint = reResource ? await encryptHint(reResource) : "";
      if (!bodyHint) throw new Error("encryption failed");
      const payload = {
        from_agent_id: fromAgentID,
        to_agent_id: toAgentID,
        parent_task_id: handoffsState.replyingTo || "",
        subject_hint: subjectHint,
        body_hint: bodyHint,
        re_resource_hint: reHint,
      };
      await api("/v1/teams/handoffs/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    } else {
      if (replying) {
        await api(
          `/v1/a2a/messages/${encodeURIComponent(handoffsState.replyingTo)}/reply`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              from_agent_id: fromAgentID,
              body: bodyText,
            }),
          },
        );
      } else {
        await api("/v1/a2a/messages", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            from_agent_id: fromAgentID,
            to_agent_id: toAgentID,
            subject,
            body: bodyText,
            re_resource: reResource,
          }),
        });
      }
    }
    if (status) {
      status.style.color = "var(--success, #22c55e)";
      status.textContent = "Sent";
    }
    setTimeout(async () => {
      handoffsState.composing = false;
      handoffsState.replyingTo = null;
      // An open thread is drawn from its cached rows; refetch them so the
      // reply and the reply count show.
      if (handoffsState.expanded) {
        handoffsState.expandedRows = await loadHandoffThread(handoffsState.expanded);
      }
      // Re-render through the legacy renderHandoffsPage (still
      // owned by bootstrap.js until the page module
      // takes over its own render).
      const legacyRender = window.AC_LEGACY?.renderHandoffsPage;
      if (typeof legacyRender === "function") await legacyRender();
    }, 600);
  } catch (e) {
    failCompose(status, sendBtn, (e && e.message) || "send failed");
  }
}

function failCompose(status, btn, msg) {
  if (status) {
    status.style.color = "var(--danger, #ef4444)";
    status.textContent = msg;
  }
  if (btn) btn.disabled = false;
}

// ---- thread drawer ----

export async function expandHandoffThread(contextID) {
  const body = document.getElementById("handoffs-body");
  if (!body) return;
  handoffsState.expanded = contextID;
  handoffsState.expandedRows = [];
  // Reflect the entered thread in the URL so it's bookmark-able.
  try {
    history.pushState(
      { page: "handoffs", threadID: contextID },
      "",
      routeURL("/handoffs?thread=" + encodeURIComponent(contextID)),
    );
  } catch (_) {
    /* pushState failure is non-fatal */
  }
  patchHTML(
    body,
    '<div class="empty"><div class="empty-title">Loading thread…</div></div>',
  );
  try {
    handoffsState.expandedRows = await loadHandoffThread(contextID);
  } catch (e) {
    patchHTML(
      body,
      `<div class="empty">
        <div class="empty-title">Could not load thread</div>
        <div class="empty-detail">${escapeHTML(e.message || "fetch failed")}</div></div>`,
    );
    return;
  }
  drawHandoffsExpanded();
}

export async function loadHandoffThread(contextID) {
  if (isHandoffsCloudMode()) {
    const data = await api(
      "/v1/teams/handoffs/threads/" + encodeURIComponent(contextID),
    );
    const raw = (data && data.handoffs) || [];
    return await Promise.all(
      raw.map(async (h) => ({
        task_id: h.task_id,
        context_id: h.context_id,
        parent_task_id: h.parent_task_id,
        from_agent_id: h.from_agent_id,
        to_agent_id: h.to_agent_id,
        from_machine_id: h.from_machine_id,
        state: h.state,
        created_at: h.created_at,
        subject: h.subject_hint
          ? (await decryptHint(h.subject_hint)) || ""
          : "",
        body: h.body_hint
          ? (await decryptHint(h.body_hint)) || "(encrypted)"
          : "",
        re_resource: h.re_resource_hint
          ? (await decryptHint(h.re_resource_hint)) || ""
          : "",
      })),
    );
  }
  const data = await api(
    "/v1/a2a/threads/" + encodeURIComponent(contextID),
  );
  return (data && data.handoffs) || [];
}

export function drawHandoffsExpanded() {
  const body = document.getElementById("handoffs-body");
  if (!body) return;
  const rows = handoffsState.expandedRows;
  if (!rows.length) {
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Empty thread</div></div>',
    );
    return;
  }
  // agc msg send carries no subject: title the thread with its first words.
  const firstBody = ((rows[0].body || "").trim().split("\n")[0] || "").trim();
  const subject = rows[0].subject || (firstBody.length > 90 ? firstBody.slice(0, 87) + "…" : firstBody) || "(no subject)";
  const lastRow = rows[rows.length - 1];
  const hasOpenRows = rows.some(
    (r) => r.state === "submitted" || r.state === "working",
  );
  const resolvedBadge = hasOpenRows
    ? ""
    : `<span class="type-badge" style="background: var(--success-soft, #14532d); color: var(--success, #4ade80);">resolved</span>`;
  let html = `
    <div style="padding: 10px 16px; font-size: 12px; color: var(--text-muted); border-bottom: 1px solid var(--border); display: flex; gap: 12px; align-items: center; flex-wrap: wrap;">
      <button id="handoffs-back" class="filter-chip" style="background: var(--bg-soft); color: var(--text); border: 1px solid var(--border); padding: 4px 10px;">&larr; Back</button>
      <strong style="font-size: 13px; color: var(--text);">${escapeHTML(subject)}</strong>
      ${resolvedBadge}
      <span style="margin-left: auto; display: flex; gap: 6px; align-items: center;">
        <span><strong>${rows.length}</strong> message${rows.length === 1 ? "" : "s"}</span>
        <button id="handoffs-reply-btn" class="filter-chip active" style="background: var(--accent); color: #fff; border: 1px solid var(--accent); padding: 4px 12px;">Reply</button>
        ${hasOpenRows ? `<button id="handoffs-resolve-btn" class="filter-chip" style="background: var(--bg-soft); color: var(--text); border: 1px solid var(--border); padding: 4px 12px;">Mark resolved</button>` : ""}
      </span>
    </div>
    <table class="lease-table coord-table" style="width: 100%;">
      <thead>
        <tr>
          <th style="width: 22%;">From</th>
          <th style="width: 22%;">To</th>
          <th style="width: 14%;">Re</th>
          <th style="width: 30%;">Body</th>
          <th style="width: 12%; text-align: right;">When</th>
        </tr>
      </thead>
      <tbody>`;
  for (const h of rows) {
    // Name the chat, not just the tool, and say "Everyone" for a
    // broadcast (an empty recipient used to render as "unknown").
    const chat = chatLabel;
    const fromShort = h.from_agent_id ? chat(h.from_agent_id) : "—";
    const toShort = h.to_agent_id ? chat(h.to_agent_id) : "Everyone";
    const reChip = h.re_resource
      ? `<code style="font-size: 11px;">${escapeHTML(friendlyResource(h.re_resource))}</code>`
      : '<span style="color: var(--text-tertiary);">—</span>';
    html += `
      <tr>
        <td data-label="From"><code style="font-size: 11px;" data-tooltip="${escapeHTML(h.from_agent_id)}">${escapeHTML(fromShort)}</code><span class="coord-to"> → <code style="font-size: 11px;">${escapeHTML(toShort)}</code></span></td>
        <td data-label="To" class="coord-hide-narrow"><code style="font-size: 11px;" data-tooltip="${escapeHTML(h.to_agent_id || "(broadcast)")}">${escapeHTML(toShort)}</code></td>
        <td data-label="Re" class="coord-hide-narrow">${reChip}</td>
        <td data-label="Message" style="font-size: 12px; line-height: 1.5; white-space: pre-wrap; padding: 8px 14px;">${escapeHTML(h.body || "")}</td>
        <td class="age-cell">${escapeHTML(ago(h.created_at))}</td>
      </tr>`;
  }
  html += "</tbody></table>";
  patchHTML(body, html);
  // patchHTML keeps these nodes when the markup is unchanged, so handlers
  // are assigned, not added: added ones stacked up and one click on Mark
  // resolved sent one request per redraw.
  const backBtn = document.getElementById("handoffs-back");
  if (backBtn)
    backBtn.onclick = () => {
      const back = handoffsState.returnPage || "";
      handoffsState.expanded = null;
      handoffsState.expandedRows = [];
      handoffsState.returnPage = "";
      if (back) {
        navigate(back, true);
      } else {
        try {
          history.pushState({ page: "handoffs" }, "", routeURL("/handoffs"));
        } catch (_) {
          /* non-fatal */
        }
        drawHandoffsBody();
      }
    };
  const replyBtn = document.getElementById("handoffs-reply-btn");
  if (replyBtn && lastRow)
    replyBtn.onclick = () => {
      startHandoffCompose(lastRow.task_id);
    };
  const resolveBtn = document.getElementById("handoffs-resolve-btn");
  if (resolveBtn && lastRow)
    resolveBtn.onclick = async () => {
      resolveBtn.disabled = true;
      resolveBtn.textContent = "Resolving…";
      try {
        await resolveHandoffThread(lastRow.task_id);
        handoffsState.expandedRows = await loadHandoffThread(
          handoffsState.expanded,
        );
        drawHandoffsExpanded();
        handoffsState.threads = await loadHandoffThreads();
        refreshHandoffsBadge();
      } catch (e) {
        showToast("error", "Resolve failed: " + (e.message || "unknown"));
        resolveBtn.disabled = false;
        resolveBtn.textContent = "✓ Mark resolved";
      }
    };
}

// resolveHandoffThread closes the thread that contains taskID via
// the right endpoint per deploy mode. Cloud mode is optimistic UI:
// the recipient daemon will see "completed" once the next reply
// (or local resolve) propagates — no coordinator round-trip yet.
export async function resolveHandoffThread(taskID) {
  if (isHandoffsCloudMode()) {
    return;
  }
  const resp = await api(
    `/v1/a2a/messages/${encodeURIComponent(taskID)}/resolve`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ thread: true }),
    },
  );
  return resp;
}

export async function refreshHandoffsBadge() {
  const badge = document.getElementById("nav-handoffs-count");
  if (!badge) return;

  // Cross-machine unread mirror. Cloud mode: per-thread unread_count
  // is always 0 (mirrored rows leave the outbound queue on ACK).
  // Instead, sum the per-machine unread snapshots pushed every ~30s.
  // Daemon mode: direct counts on the threads already.
  let total = 0;
  if (isHandoffsCloudMode()) {
    try {
      const data = await api("/v1/teams/handoffs/inbox-state");
      total = (data && data.total_unread) || 0;
    } catch {
      total = (handoffsState.threads || []).reduce(
        (s, t) => s + (t.unread_count || 0),
        0,
      );
    }
  } else {
    total = (handoffsState.threads || []).reduce(
      (s, t) => s + (t.unread_count || 0),
      0,
    );
  }

  if (total > 0) {
    badge.textContent = String(total);
    badge.style.display = "";
  } else {
    badge.style.display = "none";
  }
}
